'use client';

import ky from 'ky';
import { PACKAGE_CODE, PLATFORM_CODE } from './constants';
import type {
  ConsentResponse,
  EntitlementListResponse,
  PackageCode,
  PlatformCode,
  ResponsePayload,
  ServiceCode,
} from './types';

/**
 * 로그인
 * 구글 로그인으로 리다이렉트
 * @param authUrl - 인증 서버 URL
 * @param redirectUrl - 로그인 후 돌아올 주소 (ex. http://localhost:3020)
 */
export const login = (authUrl: string, redirectUrl: string) => {
  const encodedRedirectUrl = encodeURIComponent(redirectUrl);
  const redirectURL = `${authUrl}/v1/auth/login?redirect_url=${encodedRedirectUrl}`;
  window.location.href = redirectURL;
};

/**
 * 로그아웃
 * CORS 때문에 AJAX 요청 불가하여 form submit 방식 사용
 * @param authUrl - 인증 서버 URL
 * @param redirectUrl - 로그아웃 후 돌아올 주소 (ex. http://localhost:3020). BE에 말해서 주소 등록 필요함
 */
export const logout = (authUrl: string, redirectUrl: string) => {
  const encodedRedirectUrl = encodeURIComponent(redirectUrl);
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${authUrl}/v1/auth/logout`;

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'redirect_url';
  input.value = encodedRedirectUrl;
  form.appendChild(input);

  document.body.appendChild(form);
  form.submit();
};

// NOTE: Extracted from saas-platform authentication logic
/**
 * 엔타이틀먼트 조회
 * 사용자의 구독 정보 및 사용량 정보를 조회합니다.
 * target domain은 gateway로 proxy해야합니다.
 * @param options - 조회 옵션
 * @param options.packageCode - 패키지 코드 (기본값: PACKAGE_CODE.GAME)
 * @param options.platformCode - 플랫폼 코드 (기본값: PLATFORM_CODE.VARCO_SAAS)
 * @param options.timezone - 타임존 (기본값: 'Asia/Seoul')
 * @returns 엔타이틀먼트 목록 데이터
 */
// eslint-disable-next-line no-unused-vars -- saas-platform에서 발췌해 보존 중 (아직 미사용)
const getEntitlement = async ({
  packageCode = PACKAGE_CODE.GAME,
  platformCode = PLATFORM_CODE.VARCO_SAAS,
  timezone = 'Asia/Seoul',
}: {
  packageCode?: PackageCode;
  platformCode?: PlatformCode;
  timezone?: string;
}): Promise<EntitlementListResponse> => {
  const response = await ky.get<ResponsePayload<EntitlementListResponse>>(
    `/api/v1/entitlement/entitlements`,
    {
      headers: {
        'x-client-timezone': timezone,
        'x-package-code': packageCode,
        'x-platform-code': platformCode,
      },
    },
  );
  const payload = await response.json();
  return payload.data;
};

export const getConsents = async ({
  packageCode = PACKAGE_CODE.GAME,
  platformCode = PLATFORM_CODE.VARCO_SAAS,
  serviceCode,
}: {
  packageCode?: PackageCode;
  platformCode?: PlatformCode;
  serviceCode: ServiceCode;
}) => {
  const response = await ky
    .get<{ consents: [ConsentResponse] }>(`/api/v1/consent/consents`, {
      headers: {
        'x-package-code': packageCode,
        'x-platform-code': platformCode,
        'x-service-code': serviceCode,
      },
    })
    .json();
  return response.consents;
};

export const setConsent = async ({
  policyId,
  accepted,
  platformCode,
  packageCode,
  serviceCode,
  timezone = 'Asia/Seoul',
}: {
  policyId: string;
  accepted: boolean;
  platformCode?: PlatformCode;
  packageCode?: PackageCode;
  serviceCode?: ServiceCode;
  timezone?: string;
}) => {
  const response = await ky.put('/api/v1/consent/consents', {
    headers: {
      'x-platform-code': platformCode,
      'x-package-code': packageCode,
      'x-service-code': serviceCode,
      'x-client-timezone': timezone,
    },
    json: {
      consents: [{ policy_id: policyId, accepted: accepted }],
    },
  });
  return response;
};

export { PACKAGE_CODE, PLATFORM_CODE, SERVICE_CODE } from './constants';
